import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgMembersApi } from '@/services/api/org-members';
import { orgMemberPositionsKeys } from '@/hooks/api/useOrgMemberPositions';
import { orgProjectBudgetTreeKeys } from '@/hooks/api/useOrgProjectBudgetTree';
import { CONTEXTS_QUERY_KEY } from '@/lib/context-query-keys';
import type {
  OrgMemberRole,
  OrgMemberStatus,
  RoleTemplateCode,
  UpdateMemberIdentityDto,
  OrgReportAccessScope,
} from '@/types/org';

export interface OrgMembersListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  orgRole?: number;
  roleTemplateCode?: string;
  customGroupId?: string;
  positionId?: string;
  memberStatus?: number;
}

export const orgMembersKeys = {
  all: ['org-members'] as const,
  byOrg: (orgId: string) => ['org', orgId, 'members'] as const,
  list: (orgId: string, params: OrgMembersListParams) =>
    [...orgMembersKeys.byOrg(orgId), params] as const,
  positions: (orgId: string) => ['org', orgId, 'positions'] as const,
};

export const orgMemberProjectsKeys = {
  all: ['org-member-projects'] as const,
  detail: (orgId: string, targetUserId: string) =>
    [...orgMemberProjectsKeys.all, orgId, targetUserId] as const,
};

function invalidateOrgMembers(queryClient: ReturnType<typeof useQueryClient>, orgId: string) {
  void queryClient.invalidateQueries({ queryKey: orgMembersKeys.byOrg(orgId) });
}

export function invalidateOrgMemberProjectAccessQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
) {
  void queryClient.invalidateQueries({ queryKey: ['projects'] });
  void queryClient.invalidateQueries({
    queryKey: ['projects', 'subproject-member-management'],
  });
  void queryClient.invalidateQueries({
    queryKey: ['projects', 'subproject-member-targets'],
  });
  void queryClient.invalidateQueries({
    queryKey: ['org', orgId, 'members', 'available-for-project'],
  });
  void queryClient.invalidateQueries({ queryKey: orgMemberProjectsKeys.all });
  void queryClient.invalidateQueries({ queryKey: CONTEXTS_QUERY_KEY });
  // 与 ['projects'] 前缀不同，成员弹窗读的是 project-shares/members
  void queryClient.invalidateQueries({ queryKey: ['project-shares', 'members'], exact: false });
}

function invalidateOrgMemberPositions(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
) {
  void queryClient.invalidateQueries({ queryKey: orgMembersKeys.positions(orgId) });
  void queryClient.invalidateQueries({ queryKey: orgMemberPositionsKeys.list(orgId) });
}

export function invalidateOrgMemberIdentityRelatedQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
) {
  invalidateOrgMembers(queryClient, orgId);
  invalidateOrgMemberPositions(queryClient, orgId);
  void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'credits'] });
  void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'dashboard'] });
  void queryClient.invalidateQueries({ queryKey: CONTEXTS_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: ['project-shares', 'members'], exact: false });
  void queryClient.invalidateQueries({
    queryKey: [...orgProjectBudgetTreeKeys.all, orgId],
    exact: false,
  });
}

export function useOrgMembersList(
  orgId: string | null | undefined,
  params: OrgMembersListParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: orgMembersKeys.list(orgId ?? '', params),
    queryFn: () => orgMembersApi.listMembers(orgId!, params),
    enabled: (options?.enabled ?? true) && !!orgId,
    placeholderData: keepPreviousData,
  });
}

interface UpdateOrgMemberIdentityArgs {
  targetUserId: string;
  orgRole: OrgMemberRole;
  roleTemplateCode: RoleTemplateCode | null;
  customGroupIds: string[];
  positionIds?: string[];
  updatePositions?: boolean;
  validateProjectResidue?: boolean;
}

export function useUpdateOrgMemberIdentityMutation(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      targetUserId,
      orgRole,
      roleTemplateCode,
      customGroupIds,
      positionIds = [],
      updatePositions = false,
      validateProjectResidue = false,
    }: UpdateOrgMemberIdentityArgs) => {
      if (validateProjectResidue) {
        const residue = await orgMembersApi.getMemberProjectResidue(orgId, targetUserId);
        if (
          residue.projectMemberCount > 0 ||
          residue.functionalRoleCount > 0 ||
          residue.leadOfProjectIds.length > 0
        ) {
          throw new Error(
            `该成员仍在项目中有身份残留：项目成员 ${residue.projectMemberCount} 个 / 职能 ${residue.functionalRoleCount} 个 / 负责人 ${residue.leadOfProjectIds.length} 个，请先完成交接`
          );
        }
      }

      const dto: UpdateMemberIdentityDto = {
        orgRole,
        roleTemplateCode,
        customGroupIds,
      };
      const result = await orgMembersApi.updateMemberIdentity(orgId, targetUserId, dto);
      if (updatePositions) {
        await orgMembersApi.updateMemberPositions(orgId, targetUserId, positionIds);
      }
      return result;
    },
    onSuccess: () => {
      invalidateOrgMemberIdentityRelatedQueries(queryClient, orgId);
      invalidateOrgMembers(queryClient, orgId);
      invalidateOrgMemberPositions(queryClient, orgId);
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'credits'] });
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'reports'] });
      void queryClient.invalidateQueries({ queryKey: CONTEXTS_QUERY_KEY });
    },
  });
}

export function useOrgMemberProjectResidueMutation(orgId: string) {
  return useMutation({
    mutationFn: (targetUserId: string) =>
      orgMembersApi.getMemberProjectResidue(orgId, targetUserId),
  });
}

export function useOrgMemberProjectsTree(
  orgId: string | null | undefined,
  targetUserId: string | null | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: orgMemberProjectsKeys.detail(orgId ?? '', targetUserId ?? ''),
    queryFn: () => orgMembersApi.listMemberProjects(orgId!, targetUserId!),
    enabled: enabled && !!orgId && !!targetUserId,
    staleTime: 30 * 1000,
  });
}

export function useUpdateOrgMemberRealNameMutation(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ targetUserId, realName }: { targetUserId: string; realName: string | null }) =>
      orgMembersApi.updateMemberRealName(orgId, targetUserId, { realName }),
    onSuccess: () => {
      invalidateOrgMembers(queryClient, orgId);
      void queryClient.invalidateQueries({
        queryKey: ['org', orgId, 'members', 'available-for-project'],
      });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'credits'] });
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'reports'] });
      void queryClient.invalidateQueries({ queryKey: CONTEXTS_QUERY_KEY });
    },
  });
}

export function useUpdateReportAccessScopeMutation(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      targetUserId,
      reportAccessScope,
    }: {
      targetUserId: string;
      reportAccessScope: OrgReportAccessScope;
    }) => orgMembersApi.updateReportAccessScope(orgId, targetUserId, { reportAccessScope }),
    onSuccess: () => {
      invalidateOrgMembers(queryClient, orgId);
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'reports'] });
    },
  });
}

export function useRemoveOrgMemberMutation(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (targetUserId: string) => orgMembersApi.removeMember(orgId, targetUserId),
    onSuccess: () => {
      invalidateOrgMembers(queryClient, orgId);
      invalidateOrgMemberProjectAccessQueries(queryClient, orgId);
    },
  });
}

export function useSetOrgMemberStatusMutation(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      targetUserId,
      status,
    }: {
      targetUserId: string;
      status: Extract<OrgMemberStatus, 1 | 2>;
    }) =>
      status === 1
        ? orgMembersApi.activateMember(orgId, targetUserId)
        : orgMembersApi.suspendMember(orgId, targetUserId),
    onSuccess: () => invalidateOrgMembers(queryClient, orgId),
  });
}

export function useUpdateProducerCreditLimitMutation(orgId: string, targetUserId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (producerCreditLimit: number) =>
      orgMembersApi.updateProducerCreditLimit(orgId, targetUserId, producerCreditLimit),
    onSuccess: () => {
      invalidateOrgMembers(queryClient, orgId);
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'credits'] });
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'reports'] });
      void queryClient.invalidateQueries({ queryKey: CONTEXTS_QUERY_KEY });
    },
  });
}

export function useLeaveOrgMutation(orgId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => orgMembersApi.leaveOrg(orgId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CONTEXTS_QUERY_KEY });
    },
  });
}
